import { defineStore } from "pinia";
import { computed, toRefs, reactive, watch } from "vue";
import type { IDomainOutletAddress } from "~/domain/outlet-address";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { IDomainOrderingModeSettings } from "~/domain/ordering-mode-settings";
import type { IDomainSalesChannel } from "~/domain/sales-channels";
import {
  useAnalytics,
  useBugReporter,
  useDirectBackend,
  useFeatureFlag,
} from "#imports";

// Define the state interface
type IState = {
  isLoaded: boolean;
  id: string;
  name: string;
  slug: string;
  phone?: string;
  cover?: string;
  isTaxIncluded: boolean;
  hasDelivery: boolean;
  hasPickup: boolean;
  hasCurbside: boolean;
  address: IDomainOutletAddress;
  timezone: string;
  activeOrderingMode?: IDomainOrderingMode;
  isLoadedOrderingModeSettings: boolean;
  orderingModes: {
    delivery: IDomainOrderingModeSettings;
    pickup: IDomainOrderingModeSettings;
    curbside: IDomainOrderingModeSettings;
  };
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    id: "",
    name: "",
    slug: "",
    phone: undefined,
    cover: undefined,
    hasDelivery: false,
    hasPickup: false,
    hasCurbside: false,
    address: {
      lat: 0,
      lng: 0,
      location: "",
    },
    timezone: "",
    isTaxIncluded: false,
    activeOrderingMode: undefined,
    isLoadedOrderingModeSettings: false,
    orderingModes: {
      delivery: {
        mode: "delivery",
        isEnabled: false,
        acceptsPackaging: false,
        acceptsAsapOrders: true,
        acceptsPreOrders: true,
        outletState: "closed",
        paymentMethods: [],
        preparationMinutes: 0,
        estimatedCompletionMinutes: 0,
        slotLength: 0,
        openingHours: [],
      },
      pickup: {
        mode: "pickup",
        isEnabled: false,
        acceptsPackaging: false,
        acceptsAsapOrders: true,
        acceptsPreOrders: true,
        outletState: "closed",
        paymentMethods: [],
        preparationMinutes: 0,
        estimatedCompletionMinutes: 0,
        slotLength: 0,
        openingHours: [],
      },
      curbside: {
        mode: "curbside",
        isEnabled: false,
        acceptsPackaging: false,
        acceptsAsapOrders: true,
        acceptsPreOrders: true,
        outletState: "closed",
        paymentMethods: [],
        preparationMinutes: 0,
        estimatedCompletionMinutes: 0,
        slotLength: 0,
        openingHours: [],
      },
    },
  };
}

export const useOutletStore = defineStore("outlet", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const { outletRepo } = useDirectBackend();

  const orderingMode = computed(() => {
    if (!state.activeOrderingMode) return state.orderingModes.delivery;
    return state.orderingModes[state.activeOrderingMode];
  });

  const isClosed = computed(() => orderingMode.value.outletState === "closed");
  const isBusy = computed(() => orderingMode.value.outletState === "busy");

  const { reportGlobalTags } = useBugReporter();
  const { setContextField } = useFeatureFlag();
  const { setSuperProperties } = useAnalytics();

  watch(
    () => state.id,
    () => {
      setSuperProperties({
        outletId: state.id || undefined,
        outletName: state.name || undefined,
        outletSlug: state.slug || undefined,
      });

      setContextField("outletId", state.id || undefined);

      reportGlobalTags({
        "outlet.id": state.id || undefined,
        "outlet.slug": state.slug || undefined,
      });
    },
  );

  const loadOrderingModeSettings = async (
    salesChannel: IDomainSalesChannel,
  ) => {
    const settings = await outletRepo.getSettings({
      outletId: state.id,
      salesChannel,
      orderingMode: [state.activeOrderingMode],
    });

    const mapOrderingModes = (settings || []).map((s) => ({
      mode: s.mode,
      isEnabled: s.isEnabled,
      preparationMinutes: s.preparationMinutes,
      estimatedCompletionMinutes: s.estimatedCompletionMinutes,
      acceptsPackaging: s.acceptsPackaging,
      acceptsAsapOrders: s.acceptsAsapOrders,
      acceptsPreOrders: s.acceptsPreOrders,
      slotLength: s.slotLength,
      openingHours: s.openingHours,
      outletState: s.operationalState,
      paymentMethods: s.paymentMethods,
    }));

    patch({
      orderingModes: {
        delivery:
          mapOrderingModes.find((s) => s.mode === "delivery") ??
          state.orderingModes.delivery,
        pickup:
          mapOrderingModes.find((s) => s.mode === "pickup") ??
          state.orderingModes.pickup,
        curbside:
          mapOrderingModes.find((s) => s.mode === "curbside") ??
          state.orderingModes.curbside,
      },
      isLoadedOrderingModeSettings: true,
    });
  };

  async function loadPickupAcceptsPackaging(
    businessId: string,
    salesChannel: IDomainSalesChannel,
  ) {
    const pickupSettings = await outletRepo.getOutletBySlug({
      businessId,
      outletSlug: state.slug,
      salesChannel,
      orderingMode: "pickup",
    });

    if (pickupSettings) {
      patch({
        orderingModes: {
          ...state.orderingModes,
          pickup: {
            ...state.orderingModes.pickup,
            acceptsPackaging: pickupSettings.settings.acceptsPackaging,
          },
        },
      });
    }
  }

  async function refreshPaymentMethods(
    orderingMode: IDomainOrderingMode,
    salesChannel: IDomainSalesChannel,
  ) {
    const res = await outletRepo.getPaymentMethods({
      outletId: state.id,
      orderingMode,
      salesChannel,
    });

    patch({
      orderingModes: {
        ...state.orderingModes,
        [orderingMode]: {
          ...state.orderingModes[orderingMode],
          paymentMethods: res.data,
        },
      },
    });
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    isClosed,
    isBusy,
    orderingMode,
    loadOrderingModeSettings,
    refreshPaymentMethods,
    loadPickupAcceptsPackaging,
  };
});
